import { Navigate, Route, Routes } from 'react-router-dom';
import './styles/design-system.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import PreqGatedRoute from './components/Auth/PreqGatedRoute';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PrequalificationPage from './pages/PrequalificationPage';
import ApplicationDashboardPage from './pages/ApplicationDashboardPage';
import ApplicationFormPage from './pages/application/ApplicationFormPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminApplicationsPage from './pages/admin/AdminApplicationsPage';
import AdminApplicationDetailPage from './pages/admin/AdminApplicationDetailPage';
import AdminPrequalQuestionsPage from './pages/admin/AdminPrequalQuestionsPage';
import AdminCompaniesPage from './pages/admin/AdminCompaniesPage';
import AdminEmailLogPage from './pages/admin/AdminEmailLogPage';
import AdminEmailTemplatesPage from './pages/admin/AdminEmailTemplatesPage';
import AdminStatisticsPage from './pages/admin/AdminStatisticsPage';
import AdminSectionConfigPage from './pages/admin/AdminSectionConfigPage';
import AdminScoringRulesPage from './pages/admin/AdminScoringRulesPage';
import AdminReviewCriteriaPage from './pages/admin/AdminReviewCriteriaPage';
import AdminDeclarationConfigPage from './pages/admin/AdminDeclarationConfigPage';
import AdminChamberRegistryPage from './pages/admin/AdminChamberRegistryPage';
import AdminInvitesPage from './pages/admin/AdminInvitesPage';
import AdminHelpContentPage from './pages/admin/AdminHelpContentPage';
import AdminDeadlinesPage from './pages/admin/AdminDeadlinesPage';
import AdminDocumentsPage from './pages/admin/AdminDocumentsPage';
import AdminAiConfigPage from './pages/admin/AdminAiConfigPage';
import DocumentsPage from './pages/DocumentsPage';
import InviteAcceptPage from './pages/InviteAcceptPage';
import InviteColleaguePage from './pages/InviteColleaguePage';
import StaffInviteAcceptPage from './pages/StaffInviteAcceptPage';
import ReviewerDashboardPage from './pages/reviewer/ReviewerDashboardPage';
import ReviewerApplicationPage from './pages/reviewer/ReviewerApplicationPage';
import EditorPublicationPage from './pages/editor/EditorPublicationPage';
import ProfilePage from './pages/ProfilePage';
import AddCompanyPage from './pages/AddCompanyPage';

function HomeRedirect() {
  const { user, isLoading } = useAuth();
  if (isLoading) return null;
  const isAdmin    = user?.roles.some((r) => ['tenant_admin', 'super_admin'].includes(r)) ?? false;
  const isReviewer = user?.roles.some((r) => r === 'reviewer') ?? false;
  const isEditor   = user?.roles.some((r) => r === 'editor') ?? false;
  if (isAdmin)    return <Navigate to="/admin/applications" replace />;
  if (isReviewer) return <Navigate to="/reviewer/applications" replace />;
  if (isEditor)   return <Navigate to="/editor/publication" replace />;
  return <Navigate to="/prequalification" replace />;
}

function PlaceholderPage({ title }: { title: string }) {
  return (
    <div style={{ padding: '48px 40px', color: 'var(--primary)' }}>
      <h2 style={{ margin: 0 }}>{title}</h2>
      <p style={{ color: 'var(--muted-foreground)' }}>Ez az oldal még fejlesztés alatt áll.</p>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        {/* Public */}
        <Route path="/login"           element={<LoginPage />} />
        <Route path="/register"        element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password"  element={<ResetPasswordPage />} />
        <Route path="/invite/:token"        element={<InviteAcceptPage />} />
        <Route path="/staff-invite/:token" element={<StaffInviteAcceptPage />} />

        {/* Accessible to all logged-in users */}
        <Route path="/profile" element={
          <ProtectedRoute><ProfilePage /></ProtectedRoute>
        } />

        {/* Company-only routes: admins/reviewers are redirected to their own section */}
        <Route path="/prequalification" element={
          <ProtectedRoute companyOnly><PrequalificationPage /></ProtectedRoute>
        } />
        <Route path="/companies/add" element={
          <ProtectedRoute companyOnly><AddCompanyPage /></ProtectedRoute>
        } />

        {/* Company-only + preq-gated */}
        <Route path="/application" element={
          <ProtectedRoute companyOnly><PreqGatedRoute><ApplicationDashboardPage /></PreqGatedRoute></ProtectedRoute>
        } />
        <Route path="/application/form" element={
          <ProtectedRoute companyOnly><PreqGatedRoute><ApplicationFormPage /></PreqGatedRoute></ProtectedRoute>
        } />
        <Route path="/dashboard" element={
          <ProtectedRoute companyOnly><PreqGatedRoute><PlaceholderPage title="Áttekintés" /></PreqGatedRoute></ProtectedRoute>
        } />
        <Route path="/documents" element={
          <ProtectedRoute companyOnly><DocumentsPage /></ProtectedRoute>
        } />
        <Route path="/invite-colleague" element={
          <ProtectedRoute companyOnly><InviteColleaguePage /></ProtectedRoute>
        } />
        <Route path="/status" element={
          <ProtectedRoute companyOnly><PreqGatedRoute><PlaceholderPage title="Állapot" /></PreqGatedRoute></ProtectedRoute>
        } />

        {/* Admin */}
        <Route path="/admin/users" element={
          <ProtectedRoute requiredRoles={['tenant_admin', 'super_admin']}><AdminUsersPage /></ProtectedRoute>
        } />
        <Route path="/admin/applications" element={
          <ProtectedRoute requiredRoles={['tenant_admin', 'super_admin']}><AdminApplicationsPage /></ProtectedRoute>
        } />
        <Route path="/admin/applications/:id" element={
          <ProtectedRoute requiredRoles={['tenant_admin', 'super_admin']}><AdminApplicationDetailPage /></ProtectedRoute>
        } />
        <Route path="/admin/email-log" element={
          <ProtectedRoute requiredRoles={['tenant_admin', 'super_admin']}><AdminEmailLogPage /></ProtectedRoute>
        } />
        <Route path="/admin/email-templates" element={
          <ProtectedRoute requiredRoles={['tenant_admin', 'super_admin']}><AdminEmailTemplatesPage /></ProtectedRoute>
        } />
        <Route path="/admin/statistics" element={
          <ProtectedRoute requiredRoles={['tenant_admin', 'super_admin']}><AdminStatisticsPage /></ProtectedRoute>
        } />
        <Route path="/admin/section-config" element={
          <ProtectedRoute requiredRoles={['tenant_admin', 'super_admin']}><AdminSectionConfigPage /></ProtectedRoute>
        } />
        <Route path="/admin/scoring-rules" element={
          <ProtectedRoute requiredRoles={['tenant_admin', 'super_admin']}><AdminScoringRulesPage /></ProtectedRoute>
        } />
        <Route path="/admin/review-criteria" element={
          <ProtectedRoute requiredRoles={['tenant_admin', 'super_admin']}><AdminReviewCriteriaPage /></ProtectedRoute>
        } />
        <Route path="/admin/declaration-config" element={
          <ProtectedRoute requiredRoles={['tenant_admin', 'super_admin']}><AdminDeclarationConfigPage /></ProtectedRoute>
        } />
        <Route path="/admin/chamber-registry" element={
          <ProtectedRoute requiredRoles={['tenant_admin', 'super_admin']}><AdminChamberRegistryPage /></ProtectedRoute>
        } />
        <Route path="/admin/companies" element={
          <ProtectedRoute requiredRoles={['tenant_admin', 'super_admin']}><AdminCompaniesPage /></ProtectedRoute>
        } />
        <Route path="/admin/prequalification/questions" element={
          <ProtectedRoute requiredRoles={['tenant_admin', 'super_admin']}><AdminPrequalQuestionsPage /></ProtectedRoute>
        } />
        <Route path="/admin/invites" element={
          <ProtectedRoute requiredRoles={['tenant_admin', 'super_admin']}><AdminInvitesPage /></ProtectedRoute>
        } />
        <Route path="/admin/deadlines" element={
          <ProtectedRoute requiredRoles={['tenant_admin', 'super_admin']}><AdminDeadlinesPage /></ProtectedRoute>
        } />
        <Route path="/admin/help-content" element={
          <ProtectedRoute requiredRoles={['tenant_admin', 'super_admin']}><AdminHelpContentPage /></ProtectedRoute>
        } />
        <Route path="/admin/documents" element={
          <ProtectedRoute requiredRoles={['tenant_admin', 'super_admin']}><AdminDocumentsPage /></ProtectedRoute>
        } />
        <Route path="/admin/ai-config" element={
          <ProtectedRoute requiredRoles={['tenant_admin', 'super_admin']}><AdminAiConfigPage /></ProtectedRoute>
        } />

        {/* Reviewer */}
        <Route path="/reviewer/applications" element={
          <ProtectedRoute requiredRoles={['reviewer']}><ReviewerDashboardPage /></ProtectedRoute>
        } />
        <Route path="/reviewer/applications/:id" element={
          <ProtectedRoute requiredRoles={['reviewer']}><ReviewerApplicationPage /></ProtectedRoute>
        } />

        {/* Editor */}
        <Route path="/editor/publication" element={
          <ProtectedRoute requiredRoles={['editor']}><EditorPublicationPage /></ProtectedRoute>
        } />
        <Route path="/editor/profile" element={
          <ProtectedRoute requiredRoles={['editor']}><ProfilePage /></ProtectedRoute>
        } />

        {/* Fallback */}
        <Route path="/"  element={<HomeRedirect />} />
        <Route path="*"  element={<Navigate to="/login" replace />} />
      </Routes>
    </AuthProvider>
  );
}
